import { ReactElement, ReactNode, useCallback, useMemo, useState } from "react";
import { get, isArray, orderBy } from "lodash";
import { SortedHeadCell } from "./SortedHeadCell";
import { handleEnter } from "../utils/keyboardEvents";
import { observer } from "mobx-react";
import { Table, TableProps } from "./Table";
import { isTMessage, isTMessageParam, TMessage, TMessageParam } from "../i18n/tt";
import { useDispatch, useI18n, useTranslation } from "../stores/RootStoreContext";
import { PreppedI18nState } from "../i18n/i18nDomain";
import { IconButton, IconButtonProps, LinkButton, LinkButtonProps } from "./Button";
import { Box } from "./Box";
import { DropdownMenu, DropdownMenuItems } from "./Dropdown";
import { DCEvent } from "../events/eventsDomain";

export interface TableContext {
  shouldSkipCell?: { (rowIndex: number, columnIndex: number): boolean };
  getRowSpan?: { (rowIndex: number, columnIndex: number): number | undefined };
}

export interface TableColumn<T> {
  name: string;
  label: string | TMessage;
  width?: string;
  align?: "left" | "center" | "right";
  getValue?: (
    row: T,
    index: number,
    i18n: PreppedI18nState,
  ) => ReactNode | TMessage | TMessageParam;
  getFooterValue?: (i18n: PreppedI18nState) => ReactNode | TMessage | TMessageParam;
  getSortValue?: (row: T, index: number) => unknown;
  notSorted?: boolean;
  stopClick?: boolean;
  tooltipLabel?: string;
}

export type RowProps = { className?: string; style?: { borderTop?: string } };

export interface DataTableProps<T> extends TableProps {
  tableColumns: TableColumn<T>[];
  tableData: T[];
  onRowClick?: ((row: T) => void) | ((row: T) => DCEvent[]);
  rowFilter?: (row: T) => boolean;
  rowProps?: (row: T) => RowProps | undefined;
  getId: (row: T) => string | number;
  context?: TableContext;
  withFooterRow?: boolean;
  rowActions?: (
    row: T,
  ) => Array<
    | [type: "icon", IconButtonProps]
    | [type: "context-menu", DropdownMenuItems]
    | [type: "link", LinkButtonProps]
  >;
  initialSortBy?: string;
  initialSortDirection?: "asc" | "desc";
  className?: string;
  headerLinkClassName?: string;
}

export const DataTable = observer(function DataTable<T>({
  context,
  getId,
  onRowClick,
  rowFilter = () => true,
  tableColumns,
  tableData,
  initialSortBy,
  initialSortDirection,
  headerLinkClassName,
  ...props
}: DataTableProps<T>): ReactElement {
  const { t } = useTranslation();
  const i18n = useI18n();
  const [sortedBy, setSortedBy] = useState<string | null>(initialSortBy ?? null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">(
    initialSortDirection ?? "desc",
  );
  const sortByIterator = useMemo(() => {
    const col = tableColumns.find((col) => col.name === sortedBy);
    return col && col.getSortValue
      ? (row: T, index: number) => col.getSortValue!(row, index)
      : sortedBy;
  }, [sortedBy, tableColumns]);
  const renderData = sortByIterator
    ? orderBy(tableData, [sortByIterator], [sortDirection])
    : tableData;

  const sortClick = useCallback(
    (name: string | null) => {
      setSortDirection(sortDirection === "desc" ? "asc" : "desc");
      setSortedBy(name);
    },
    [sortDirection],
  );

  const renderValue = (row: T, index: number, col: TableColumn<T>) => {
    if (col.getValue) {
      const val = col.getValue(row, index, i18n);
      return isTMessage(val) || isTMessageParam(val) ? i18n.tf(val) : val;
    }
    return get(row, col.name);
  };

  const renderFooterVal = (col: TableColumn<T>) => {
    const val = col.getFooterValue?.(i18n);
    return isTMessage(val) || isTMessageParam(val) ? i18n.tf(val) : val;
  };

  const dispatch = useDispatch();
  const handleRowClick = (row: T): (() => void) => {
    return () => {
      if (onRowClick) {
        const result = onRowClick(row);
        if (isArray(result)) {
          dispatch(result);
        }
      }
    };
  };

  return (
    <Table hoverable={!!onRowClick} {...props}>
      <thead>
        <tr>
          {tableColumns.map(({ name, label, align, notSorted, width = "auto", tooltipLabel }) =>
            notSorted ? (
              <th
                key={String(name)}
                style={{ width }}
                align={align || "right"}
                className={
                  align === "left"
                    ? "tw-text-left"
                    : align === "center"
                      ? "tw-text-center"
                      : "tw-text-right"
                }
              >
                <span className={"tw-text-sm"}>{isTMessage(label) ? t(label) : label}</span>
              </th>
            ) : (
              <SortedHeadCell
                key={String(name)}
                label={typeof label === "string" ? label : t(label)}
                align={align}
                style={{ width }}
                onClick={() => sortClick(name)}
                active={sortedBy === name}
                direction={sortDirection}
                tooltipLabel={tooltipLabel}
                linkClassName={headerLinkClassName}
              />
            ),
          )}
          {props.rowActions && <th style={{ width: "1%" }} />}
        </tr>
      </thead>
      <tbody>
        {renderData.filter(rowFilter).map((row, rowIndex) => (
          <tr
            tabIndex={onRowClick ? 0 : -1}
            key={getId(row)}
            onKeyDown={handleEnter(() => handleRowClick(row))}
            {...props.rowProps?.(row)}
          >
            {tableColumns.map((col, columnIndex) =>
              context?.shouldSkipCell?.(rowIndex, columnIndex) ? null : (
                <td
                  onClick={col.stopClick ? () => null : handleRowClick(row)}
                  style={{ width: col.width }}
                  key={String(col.name)}
                  align={col.align || "right"}
                  rowSpan={context?.getRowSpan?.(rowIndex, columnIndex)}
                >
                  {renderValue(row, rowIndex, col)}
                </td>
              ),
            )}
            {props.rowActions && (
              <td>
                <Box alignItems={"center"} justifyContent={"end"}>
                  {props.rowActions(row).map(([type, props], i) => {
                    switch (type) {
                      case "icon":
                        return <IconButton key={i} {...props} />;
                      case "link":
                        return <LinkButton key={i} {...props} />;
                      case "context-menu":
                        return (
                          <DropdownMenu
                            key={i}
                            button={{ type: "dotdotdot" }}
                            alignment={"end"}
                            items={props}
                          />
                        );
                    }
                  })}
                </Box>
              </td>
            )}
          </tr>
        ))}
      </tbody>
      {props.withFooterRow && (
        <tfoot>
          <tr>
            {tableColumns.map((col, i) =>
              context?.shouldSkipCell?.(renderData.length, i) ? null : (
                <td key={col.name} align={col.align || "right"} className={"bold"}>
                  {renderFooterVal(col)}
                </td>
              ),
            )}
          </tr>
        </tfoot>
      )}
    </Table>
  );
});
